<template>
    <div class="upload-content">
        <div class="upload-description">
            {{ title }}
        </div>

        <!-- File Upload Area -->
        <div
            class="upload-area"
            :class="{ 'upload-area-dragging': isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onFileDrop"
        >
            <font-awesome-icon icon="cloud-upload" class="upload-icon mb-4 opacity-50"/>
            <div class="upload-text">
                <label for="file-upload" class="upload-label">
                  <span class="upload-link">
                    Klik hier om een bestand te selecteren
                  </span>
                    <span class="upload-hint"> of sleep het bestand hierheen</span>
                </label>
                <input
                    id="file-upload"
                    type="file"
                    :accept="accept"
                    @change="handleFileUpload"
                    class="d-none"
                />
            </div>
            <div v-if="selectedFile" class="selected-file">
                Geselecteerd bestand: {{ selectedFile.name }}
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="isUploading" class="loading-container">
            <div class="loading-content">
                <font-awesome-icon icon="spinner" spin/>
                <span>Bestand wordt geüpload...</span>
            </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="selectedFile !== null" class="button-container">
            <b-button
                icon="faUndo"
                @click="resetUpload"
            >
                <FontAwesomeIcon icon="undo" class="ml-1" />
                Nieuw bestand uploaden
            </b-button>
        </div>

        <!-- Extra form data -->
        <div v-if="formFields?.length > 0">
            <b-row v-for="(formField, index) in formFields" :key="index" class="mb-1">
                <b-col sm="2"><label :for="formField.name">{{ formField.title }}</label></b-col>
                <b-col sm="2">
                    <b-input
                        style="width: 300px"
                        type="text"
                        :placeholder="formField.placeholder"
                        v-model="formField.value"
                        class="d-inline-block mr-2"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col sm="2">
                    <OmniButton type="submit"
                                variant="primary"
                                class="btn btn-xs"
                                :disabled="!isFormComplete && !isUploading"
                                @click="processFile(true)"
                    >Upload</OmniButton>
                </b-col>
            </b-row>
        </div>

        <!-- Instructions -->
        <div v-if="instructions && instructions.length > 0 && !isFormComplete" class="alert alert-info instructions">
            <h4 class="instructions-title">
                <font-awesome-icon icon="circle-info"/>
                Instructies
            </h4>

            <ul class="instructions-list">
                <li v-for="(instruction, index) in instructions" :key="index">{{instruction}}</li>
            </ul>
        </div>
    </div>
</template>

<script>

import OmniButton from "@/components/OmniStock/OmniButton.vue";

export default {
    name: "FileUpload",
    components: {OmniButton},

    data() {
        return {
            isDragging: false,
            isUploading: false,
            selectedFile: null,
        }
    },

    props: {
        title: {required: false},
        instructions: {required: false},
        url: {required: true},
        formFields: {required: false},
        accept: {required: true},
    },

    computed: {
        isFormComplete() {
            if (this.formFields) {
                for (const formField of this.formFields) {
                    if (formField.value === "") {
                        return false;
                    }
                }
            }

            return this.selectedFile !== null;
        },
    },

    methods: {
        requiresManualUpload() {
            return this.formFields?.length > 0
        },

        onFileDrop: function(event) {
            this.isDragging = false
            const file = event.dataTransfer.files[0]
            if (!file) return

            this.selectedFile = file

            this.processFile()
        },

        processFile: function(manualUpload) {
            if (!this.isFormComplete) {
                return;
            }

            if (this.requiresManualUpload() && !manualUpload) {
                return;
            }

            this.isUploading = true

            let formData = new FormData();
            formData.append("upload_file", this.selectedFile);
            if (this.formFields) {
                for (const formField of this.formFields) {
                    formData.append(formField.name, formField.value);
                }
            }

            this.$http.post(this.url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            }).then(response => {
                this.isUploading = false
                this.$emit('success', this.selectedFile, response)
            }).catch(e => {
                this.isUploading = false
                this.$emit('error', this.selectedFile, e)
            })
        },
        resetUpload: function() {
            this.selectedFile = null
            this.isUploading = false

            if (this.formFields) {
                for (const formField of this.formFields) {
                    formField.value = "";
                }
            }

            const fileInput = document.getElementById('file-upload')
            if (fileInput) fileInput.value = ''

            this.$emit("reset")
        },
        handleFileUpload: function(event) {
            const file = event.target.files?.[0]
            if (!file) return

            this.selectedFile = file

            this.processFile()
        },
    },
};
</script>

<style scoped>
/* Bootstrap CSS wordt hier toegevoegd */

.upload-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.upload-description {
    font-size: 0.875rem;
    color: #6c757d;
}

/* Upload Area */
.upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 0.5rem;
    padding: 2rem;
    text-align: center;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
}

.upload-area:hover {
    border-color: #adb5bd;
}

.upload-area-dragging {
    border-color: #0d6efd !important;
    background-color: #f8f9fa;
}

.upload-icon {
    font-size: 64px;
    color: lightgrey;
}

.upload-text {
    margin-bottom: 0.5rem;
}

.upload-label {
    cursor: pointer;
    text-decoration: none;
}

.upload-link {
    color: #0d6efd;
    font-weight: 500;
}

.upload-link:hover {
    color: #0a58ca;
}

.upload-hint {
    color: #6c757d;
}

.selected-file {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #6c757d;
}

/* Loading State */
.loading-container {
    text-align: center;
}

.loading-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #0d6efd;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Button Container */
.button-container {
    text-align: center;
}

/* Instructions */
.instructions {
    margin-bottom: 0;
}

.instructions-title {
    font-size: 1rem;
    margin-bottom: 0.5rem;
}

.instructions-list {
    font-size: 0.875rem;
    margin-bottom: 0;
    padding-left: 1.25rem;
}

.instructions-list li {
    margin-bottom: 0.25rem;
}

.instructions-list li:last-child {
    margin-bottom: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
    .upload-area {
        padding: 1.5rem;
    }
}
</style>